import { getNextUpsell } from './get-next-upsell.use-case';
export const SHIPPING_COMPONENT = 'upsell-shipping';
export const EMPTY_CART_COMPONENT = 'upsell-empty-cart';
export const ORDER_SUMMARY_COMPONENT = 'order-summary';
export function maxTreeDepth(tree) {
    if (!tree || tree.length === 0)
        return 0;
    const node = tree[0];
    return 1 + Math.max(maxTreeDepth(node.onPurchase), maxTreeDepth(node.onDecline));
}
export function findNodeById(tree, id) {
    if (!tree)
        return null;
    for (const node of tree) {
        if (node.id === id)
            return node;
        if (node.onPurchase) {
            const found = findNodeById(node.onPurchase, id);
            if (found)
                return found;
        }
        if (node.onDecline) {
            const found = findNodeById(node.onDecline, id);
            if (found)
                return found;
        }
    }
    return null;
}
function buildCursorForNode(node) {
    if (node.isPhysical && node.physicalList && node.physicalList.length > 0) {
        return {
            kind: 'physical',
            bundleNodeId: node.id,
            bundleSize: node.physicalList.length,
            step: 0,
        };
    }
    return { kind: 'node', nodeId: node.id };
}
export function viewFromCursor(cursor, tree) {
    if (!cursor)
        return null;
    switch (cursor.kind) {
        case 'completed':
            return { componentName: ORDER_SUMMARY_COMPONENT, isPhysicalBundle: false };
        case 'node':
            return { componentName: cursor.nodeId, isPhysicalBundle: false };
        case 'shipping':
            return { componentName: SHIPPING_COMPONENT, isPhysicalBundle: false };
        case 'empty-cart':
            return { componentName: EMPTY_CART_COMPONENT, isPhysicalBundle: false };
        case 'physical': {
            const bundle = findNodeById(tree, cursor.bundleNodeId);
            const list = bundle?.physicalList ?? [];
            const componentName = list[cursor.step] ?? cursor.bundleNodeId;
            const isLast = cursor.step === cursor.bundleSize - 1;
            const isOnlyOne = cursor.bundleSize === 1;
            const showBackInBundle = (cursor.step > 0 || isLast) && !isOnlyOne;
            return {
                componentName,
                isPhysicalBundle: true,
                physicalStep: cursor.step,
                isLastPhysicalStep: isLast,
                showBackInBundle,
            };
        }
    }
}
export class UpsellSequence {
    constructor() {
        this.state = {
            sequence: null,
            sequenceTree: null,
            currentSequenceTree: null,
            cursor: null,
            progressIndex: 0,
            progressTotal: 0,
        };
    }
    initialize(params) {
        const { sequence, sequenceTree } = params;
        if (!sequenceTree || sequenceTree.length === 0) {
            this.state = {
                sequence: null,
                sequenceTree: null,
                currentSequenceTree: null,
                cursor: null,
                progressIndex: 0,
                progressTotal: 0,
            };
            return { kind: 'no-sequence' };
        }
        const cursor = buildCursorForNode(sequenceTree[0]);
        this.state = {
            sequence,
            sequenceTree,
            currentSequenceTree: sequenceTree,
            cursor,
            progressIndex: 0,
            progressTotal: maxTreeDepth(sequenceTree),
        };
        return { kind: 'ready', node: viewFromCursor(cursor, sequenceTree) };
    }
    next(outcome) {
        const cursor = this.state.cursor;
        if (!cursor || cursor.kind === 'completed')
            return { kind: 'completed' };
        if (cursor.kind === 'node') {
            return this.advanceFromOuterNode(cursor.nodeId, outcome.kind === 'purchased');
        }
        if (cursor.kind === 'physical') {
            if (cursor.step < cursor.bundleSize - 1) {
                return this.commitCursor({
                    kind: 'physical',
                    bundleNodeId: cursor.bundleNodeId,
                    bundleSize: cursor.bundleSize,
                    step: cursor.step + 1,
                });
            }
            if (outcome.kind === 'purchased') {
                return this.commitCursor({ kind: 'shipping', bundleNodeId: cursor.bundleNodeId });
            }
            return this.advanceFromOuterNode(cursor.bundleNodeId, false);
        }
        if (cursor.kind === 'shipping' || cursor.kind === 'empty-cart') {
            return this.advanceFromOuterNode(cursor.bundleNodeId, outcome.kind === 'purchased');
        }
        return { kind: 'completed' };
    }
    advanceWithinBundle() {
        const cursor = this.state.cursor;
        if (!cursor || cursor.kind !== 'physical') {
            return cursor
                ? { kind: 'render', node: viewFromCursor(cursor, this.state.currentSequenceTree) }
                : { kind: 'completed' };
        }
        if (cursor.step < cursor.bundleSize - 1) {
            return this.commitCursor({
                kind: 'physical',
                bundleNodeId: cursor.bundleNodeId,
                bundleSize: cursor.bundleSize,
                step: cursor.step + 1,
            });
        }
        return this.commitCursor({ kind: 'shipping', bundleNodeId: cursor.bundleNodeId });
    }
    transitionToEmptyCart() {
        const cursor = this.state.cursor;
        if (cursor?.kind === 'shipping' || cursor?.kind === 'physical' || cursor?.kind === 'empty-cart') {
            return this.commitCursor({ kind: 'empty-cart', bundleNodeId: cursor.bundleNodeId });
        }
        return { kind: 'completed' };
    }
    goBackInBundle() {
        const cursor = this.state.cursor;
        if (!cursor)
            return { kind: 'completed' };
        if (cursor.kind === 'physical') {
            if (cursor.step > 0) {
                return this.commitCursor({
                    kind: 'physical',
                    bundleNodeId: cursor.bundleNodeId,
                    bundleSize: cursor.bundleSize,
                    step: cursor.step - 1,
                });
            }
            return { kind: 'render', node: viewFromCursor(cursor, this.state.currentSequenceTree) };
        }
        if (cursor.kind === 'shipping' || cursor.kind === 'empty-cart') {
            const bundle = findNodeById(this.state.currentSequenceTree, cursor.bundleNodeId);
            const size = bundle?.physicalList?.length ?? 1;
            return this.commitCursor({
                kind: 'physical',
                bundleNodeId: cursor.bundleNodeId,
                bundleSize: size,
                step: size - 1,
            });
        }
        return { kind: 'render', node: viewFromCursor(cursor, this.state.currentSequenceTree) };
    }
    currentNode() {
        return viewFromCursor(this.state.cursor, this.state.currentSequenceTree);
    }
    isCompleted() {
        return this.state.cursor?.kind === 'completed';
    }
    progress() {
        return {
            index: this.state.progressIndex,
            total: this.state.progressTotal,
        };
    }
    snapshot() {
        return {
            sequence: this.state.sequence,
            sequenceTree: this.state.sequenceTree,
            currentSequenceTree: this.state.currentSequenceTree,
            cursor: this.state.cursor,
            progressIndex: this.state.progressIndex,
            progressTotal: this.state.progressTotal,
        };
    }
    restore(snapshot) {
        this.state = {
            sequence: snapshot.sequence,
            sequenceTree: snapshot.sequenceTree,
            currentSequenceTree: snapshot.currentSequenceTree,
            cursor: snapshot.cursor,
            progressIndex: snapshot.progressIndex ?? 0,
            progressTotal: snapshot.progressTotal ?? maxTreeDepth(snapshot.sequenceTree),
        };
    }
    reset() {
        this.state = {
            sequence: null,
            sequenceTree: null,
            currentSequenceTree: null,
            cursor: null,
            progressIndex: 0,
            progressTotal: 0,
        };
    }
    advanceFromOuterNode(currentId, wasPurchased) {
        const nextSubSequence = getNextUpsell({
            sequenceTree: this.state.currentSequenceTree,
            currentUpsellId: currentId,
            wasPurchased,
        });
        if (!nextSubSequence || nextSubSequence.length === 0) {
            this.state.currentSequenceTree = null;
            this.state.cursor = { kind: 'completed' };
            return { kind: 'completed' };
        }
        this.state.currentSequenceTree = nextSubSequence;
        const nextCursor = buildCursorForNode(nextSubSequence[0]);
        this.state.progressIndex += 1;
        return this.commitCursor(nextCursor);
    }
    commitCursor(cursor) {
        this.state.cursor = cursor;
        if (cursor.kind === 'completed')
            return { kind: 'completed' };
        return { kind: 'render', node: viewFromCursor(cursor, this.state.currentSequenceTree) };
    }
}
